/** Zones, biomes and the water table.
 *
 * The failure this file exists to avoid is a map that reads as noise: snow
 * touching sand touching swamp, a coastline made of single cells, a lake that
 * is really a scatter of puddles. Three rules do the work.
 *
 *  1. Terrain is a property of a *zone*, never of a cell. Zones are grown by a
 *     multi-source flood whose per-zone step budget varies each round, which
 *     gives organic blobs instead of Voronoi polygons.
 *  2. Two zones may only touch if their terrains are plausible neighbours
 *     (`ADJACENT`). Where the layout forces an implausible pair, a bridging
 *     terrain is painted along the seam rather than leaving the hard edge.
 *  3. Land that touches water becomes beach, so a coast is a coast and not a
 *     terrain boundary that happens to be wet on one side.
 */

import type { AdventureMapState, FactionId, TerrainKind } from "@game/core";
import { TERRAINS, setTerrain, setVariant } from "@game/core";
import type { Grid } from "./grid.js";
import { allNeighbours, idx, orthoNeighbours, xOf, yOf } from "./grid.js";
import { DIR16, type Rng, int, pick, shuffle } from "./rng.js";

export type ZoneRole = "player" | "neutral" | "water";

/**
 * What a zone is *for*, once the adjacency graph exists.
 *
 * Role is who owns the seed (player / leftover land / water). Job is what
 * the object pass is allowed to spend the zone on: a starting neighbourhood
 * is not a treasure vault, and a cul-de-sac is not a shopping street.
 */
export type ZoneJob = "starting" | "junction" | "treasure" | "dead-end" | "water";

export interface Zone {
  readonly id: number;
  readonly role: ZoneRole;
  /** Player index for a `player` zone, -1 otherwise. */
  readonly player: number;
  readonly seed: number;
  terrain: TerrainKind;
  readonly cells: number[];
  /** Filled by `assignZoneJobs` after adjacency is known. */
  job: ZoneJob;
  /**
   * Faction the zone dresses as. Player zones take their owner; neutrals take
   * a native of the terrain. Null until `assignZoneThemes` runs.
   */
  theme: FactionId | null;
}

/**
 * Which terrains may share a border. Symmetric by construction (asserted in
 * the test). Rock is structural and is excluded: it is painted as a ridge, not
 * grown as a biome.
 */
export const ADJACENT: Readonly<Record<TerrainKind, readonly TerrainKind[]>> = Object.freeze({
  water:  ["sand", "swamp", "grass", "dirt", "snow", "rough", "highlands"],
  sand:   ["water", "dirt", "rough", "grass", "lava", "wasteland"],
  dirt:   ["water", "sand", "grass", "rough", "swamp", "lava", "cavern", "snow",
           "highlands", "wasteland"],
  grass:  ["water", "sand", "dirt", "rough", "swamp", "highlands"],
  snow:   ["water", "dirt", "rough", "cavern", "highlands"],
  swamp:  ["water", "dirt", "grass", "rough"],
  rough:  ["water", "sand", "dirt", "grass", "snow", "swamp", "lava", "cavern",
           "highlands", "wasteland"],
  cavern: ["dirt", "snow", "rough", "lava", "wasteland"],
  lava:   ["sand", "dirt", "rough", "cavern", "wasteland"],
  rock:   [],
  // Upland moor sits between the green country and the cold, which is where
  // Prismgate belongs; it never touches lava or the industrial barrens.
  highlands: ["water", "grass", "dirt", "rough", "snow"],
  // Poisoned barrens are what badlands become downwind of a Cogworks, so they
  // border the dry and the burnt and nothing green.
  wasteland: ["sand", "dirt", "rough", "cavern", "lava"],
});

const LAND: readonly TerrainKind[] = Object.freeze(
  TERRAINS.filter((t) => t !== "water" && t !== "rock"),
);

export const plausible = (a: TerrainKind, b: TerrainKind): boolean =>
  a === b || ADJACENT[a].includes(b);

/** A terrain that may sit between two that may not touch, or null. */
export function bridgeTerrain(a: TerrainKind, b: TerrainKind): TerrainKind | null {
  for (const t of LAND) {
    if (t !== a && t !== b && plausible(t, a) && plausible(t, b)) return t;
  }
  return null;
}

// ------------------------------------------------------------------- seeding

/**
 * Where player `p` lands for a given rotation of the start ring.
 *
 * The ring is projected onto the map's bounding *box*, not onto a circle
 * inscribed in it: the direction vector is divided by its own largest
 * component, so every start sits at the same Chebyshev radius from the centre
 * whichever of the sixteen compass directions it drew. A circle would put a
 * diagonal start at 0.71r and an axial one at r, and Chebyshev is the metric
 * that matters here because a diagonal step on the adventure map costs exactly
 * what a straight one costs - a start "closer" only in Euclid is genuinely
 * closer to walk to.
 *
 * `PLAYER_RING_BP` of the half-extent, rather than all of it, keeps the seed
 * far enough inside the border that its zone can still grow in every
 * direction; a start pinned to the very edge grows a half-zone.
 */
const PLAYER_RING_BP = 9000;

function ringSeed(g: Grid, players: number, spin: number, p: number): number {
  const cx = g.width >> 1, cy = g.height >> 1;
  const rx = Math.max(3, Math.trunc((((g.width >> 1) - 3) * PLAYER_RING_BP) / 10000));
  const ry = Math.max(3, Math.trunc((((g.height >> 1) - 3) * PLAYER_RING_BP) / 10000));
  const d = DIR16[(spin + Math.trunc((p * 16) / players)) % 16]!;
  const m = Math.max(Math.abs(d[0]), Math.abs(d[1]));
  const x = Math.min(g.width - 4, Math.max(3, cx + Math.trunc((d[0] * rx) / m)));
  const y = Math.min(g.height - 4, Math.max(3, cy + Math.trunc((d[1] * ry) / m)));
  return idx(g, x, y);
}

/** Chebyshev gap between the two closest starts, for one rotation. */
function ringGapAt(g: Grid, players: number, spin: number): number {
  let worst = 0x3fffffff;
  for (let a = 0; a < players; a++) {
    for (let b = a + 1; b < players; b++) {
      const ia = ringSeed(g, players, spin, a), ib = ringSeed(g, players, spin, b);
      const d = Math.max(
        Math.abs(xOf(g, ia) - xOf(g, ib)), Math.abs(yOf(g, ia) - yOf(g, ib)));
      if (d < worst) worst = d;
    }
  }
  return worst;
}

/**
 * The rotations that spread the starts furthest, and how far that is.
 *
 * The rotation is **chosen rather than rolled**, because with four players it
 * is the whole difference between the four map corners (gap 2r) and the four
 * edge midpoints (gap r) - the same map, the same seeds, twice the marching.
 * Every rotation that ties for best is returned so the roll still picks among
 * them and two maps of the same size do not open identically.
 */
export function startRing(g: Grid, players: number): { gap: number; spins: number[] } {
  let gap = -1;
  const spins: number[] = [];
  for (let spin = 0; spin < 16; spin++) {
    const at = ringGapAt(g, players, spin);
    if (at > gap) { gap = at; spins.length = 0; }
    if (at === gap) spins.push(spin);
  }
  return { gap, spins };
}

/**
 * How a map's water is ARRANGED, as opposed to how much of it there is.
 *
 * `waterBp` says how many of the zones are sea; it says nothing about where
 * they end up, so every map came out the same way - two or three mid-sized
 * blobs wherever the seed scatter happened to drop them. The same quantity of
 * water reads as a completely different board depending on its shape, and a
 * generator that only ever draws one of them is a generator with one map in it.
 *
 *   lake       one sea in the middle, and the whole game is fought round it
 *   coast      the sea along one or two edges; a mainland with a shoreline
 *   islands    sea everywhere and a coast broken into bays and peninsulas
 *   patchwork  many small seas threaded through the land
 *   rivers     little standing water, but wide channels cut across everything
 *              (the channels themselves are `waterways.ts`)
 *
 * `islands` is NOT a boat-required archipelago, and the difference matters.
 * `repairConnectivity` puts every zone worth content on one landmass - a town
 * on an island nothing can reach is a stalemate, and the reachability audit
 * answers land it cannot walk to by deleting what stands on it, not by finding
 * a boat. So this shape gives a sea-dominated board with a deeply broken
 * coastline; a true waterworld needs the audit to understand sea travel first.
 */
export type WaterShape = "lake" | "coast" | "islands" | "patchwork" | "rivers";

export const WATER_SHAPES: readonly WaterShape[] =
  Object.freeze(["lake", "coast", "islands", "patchwork", "rivers"]);

/**
 * How much board a shape leaves dry, as a fraction, measured over four seeds at
 * 108x108. Used to keep a wet shape off a board that cannot afford one.
 */
const DRY_SHARE: Readonly<Record<WaterShape, number>> = Object.freeze({
  lake: 0.79, coast: 0.68, islands: 0.41, patchwork: 0.80, rivers: 0.83,
});

/**
 * The shapes this board can carry, given how many players have to live on it.
 *
 * A shape is a look until it takes the ground away from somebody. `islands`
 * leaves two fifths of the board dry; on 48x48 with eight players that is a
 * hundred and eighteen cells each against a floor of a hundred and fifty, and
 * the eighth player's town has nowhere to go - measured, seed 4242, and the
 * generator dropped a player rather than complaining. The subterranean gates
 * went the same way for want of coastline to open a mouth on.
 *
 * So the roll is taken from the shapes that leave enough dry, and a board with
 * room for all of them gets all of them.
 */
export function shapesFor(cells: number, players: number): readonly WaterShape[] {
  // The generator's own floor, with room over it: a player wants somewhere to
  // expand into, not just somewhere to stand.
  const needed = players * 260;
  const fits = WATER_SHAPES.filter((sh) => cells * DRY_SHARE[sh] >= needed);
  return fits.length > 0 ? fits : ["rivers"];
}

/**
 * How fast a water zone grows against a land zone of the same age.
 *
 * `growZones` gives every zone the same step budget per round, which produces
 * zones of roughly equal size - fine when they are all just regions, useless
 * when one of them is supposed to be a lake the map is arranged around. The
 * weight multiplies that budget.
 */
const WATER_WEIGHT: Readonly<Record<WaterShape, number>> = Object.freeze({
  lake: 2, coast: 2, islands: 3, patchwork: 1, rivers: 1,
});

/** Seed cells: players on a ring, then neutral and water zones spread apart. */
function seedCells(
  g: Grid, r: Rng, players: number, neutral: number, water: number,
  shape: WaterShape,
): { seed: number; role: ZoneRole; player: number }[] {
  const out: { seed: number; role: ZoneRole; player: number }[] = [];
  const ring = startRing(g, players);
  const spin = ring.spins[int(r, ring.spins.length)] ?? 0;
  for (let p = 0; p < players; p++) {
    out.push({ seed: ringSeed(g, players, spin, p), role: "player", player: p });
  }

  const minSep = Math.max(4, Math.trunc(Math.min(g.width, g.height) / (players + 3)));
  const far = (i: number, sep: number): boolean =>
    out.every((o) => Math.max(
      Math.abs(xOf(g, o.seed) - xOf(g, i)),
      Math.abs(yOf(g, o.seed) - yOf(g, i)),
    ) >= sep);

  const place = (role: ZoneRole, count: number, sep: number): void => {
    for (let k = 0; k < count; k++) {
      let chosen = -1;
      for (let attempt = 0; attempt < 200 && chosen < 0; attempt++) {
        const i = idx(g, 1 + int(r, g.width - 2), 1 + int(r, g.height - 2));
        if (far(i, sep - Math.trunc(attempt / 40))) chosen = i;
      }
      if (chosen < 0) chosen = idx(g, 1 + int(r, g.width - 2), 1 + int(r, g.height - 2));
      out.push({ seed: chosen, role, player: -1 });
    }
  };
  placeWater(g, r, out, water, shape, minSep, place);
  place("neutral", neutral, minSep);
  return out;
}

/**
 * Where the sea starts, which is what the shape mostly comes down to.
 *
 * A lake is one seed at the middle; a coast is seeds pressed against one or two
 * edges; islands and patchwork scatter, and rivers barely seed at all because
 * their water is cut afterwards rather than grown.
 */
function placeWater(
  g: Grid, r: Rng, out: { seed: number; role: ZoneRole; player: number }[],
  water: number, shape: WaterShape, minSep: number,
  scatter: (role: ZoneRole, count: number, sep: number) => void,
): void {
  const at = (x: number, y: number): number =>
    idx(g, Math.max(1, Math.min(g.width - 2, Math.trunc(x))),
        Math.max(1, Math.min(g.height - 2, Math.trunc(y))));
  const jitter = (span: number): number => int(r, span) - Math.trunc(span / 2);

  if (shape === "lake") {
    // One sea, at the middle, and everything else has to go round it. More than
    // one seed here and the "lake" is two ponds.
    out.push({
      seed: at(g.width / 2 + jitter(g.width / 8), g.height / 2 + jitter(g.height / 8)),
      role: "water", player: -1,
    });
    return;
  }
  if (shape === "coast") {
    // One edge, or two adjacent ones for a bay. Seeds are laid ALONG the edge
    // so the sea grows inward as a front rather than as a circle.
    const side = int(r, 4);
    const n = Math.max(2, water);
    for (let k = 0; k < n; k++) {
      const t = (k + 0.5) / n;
      const [x, y] = side === 0 ? [t * g.width, 1]
        : side === 1 ? [g.width - 2, t * g.height]
        : side === 2 ? [t * g.width, g.height - 2]
        : [1, t * g.height];
      out.push({ seed: at(x + jitter(6), y + jitter(6)), role: "water", player: -1 });
    }
    return;
  }
  if (shape === "rivers") {
    // Channels are cut later, so the sea here is one modest body to run them to.
    scatter("water", 1, minSep);
    return;
  }
  // islands and patchwork: scatter, islands with more of them.
  scatter("water", Math.max(shape === "islands" ? 4 : 1, water), minSep);
}

// ------------------------------------------------------------------- growing

/**
 * Grow every zone at once. The per-round step budget is `1..3` cells, so zones
 * that happen to roll high spread further and the borders come out ragged;
 * a constant budget produces visibly straight Voronoi seams.
 */
export function growZones(
  m: AdventureMapState, r: Rng, players: number, neutral: number, water: number,
  shape: WaterShape = "patchwork",
): Zone[] {
  const g: Grid = { width: m.width, height: m.height };
  const n = g.width * g.height;
  const seeds = seedCells(g, r, players, neutral, water, shape);
  const weightOf = (z: number): number =>
    seeds[z]!.role === "water" ? WATER_WEIGHT[shape] : 1;

  const owner = new Int32Array(n).fill(-1);
  const queues: number[][] = [];
  const heads: number[] = [];
  seeds.forEach((s, z) => {
    if (owner[s.seed] === -1) owner[s.seed] = z;
    queues.push([s.seed]);
    heads.push(0);
  });

  const buf: number[] = [];
  let remaining = n - seeds.length;
  while (remaining > 0) {
    let progress = false;
    for (let z = 0; z < seeds.length; z++) {
      const q = queues[z]!;
      let steps = weightOf(z) * (1 + int(r, 3));
      while (steps-- > 0 && heads[z]! < q.length) {
        const cur = q[heads[z]!]!;
        heads[z] = heads[z]! + 1;
        const nbs = shuffle(r, allNeighbours(g, cur, buf).slice());
        for (const nb of nbs) {
          if (owner[nb] !== -1) continue;
          owner[nb] = z;
          q.push(nb);
          remaining--;
          progress = true;
        }
      }
    }
    if (!progress) break;
  }
  // Anything the flood could not reach (it cannot happen on a rectangle, but a
  // zone list of length zero would loop) falls to zone 0.
  for (let i = 0; i < n; i++) if (owner[i] === -1) owner[i] = 0;

  const zones: Zone[] = seeds.map((s, z) => ({
    id: z, role: s.role, player: s.player, seed: s.seed,
    terrain: "grass" as TerrainKind, cells: [],
    job: s.role === "player" ? "starting" : s.role === "water" ? "water" : "junction",
    theme: null,
  }));
  for (let i = 0; i < n; i++) zones[owner[i]!]!.cells.push(i);
  return zones;
}

/** Zone id per cell, for placement budgets and for diagnostics. */
export function zoneMap(m: AdventureMapState, zones: readonly Zone[]): Int32Array {
  const out = new Int32Array(m.width * m.height).fill(-1);
  for (const z of zones) for (const i of z.cells) out[i] = z.id;
  return out;
}

/** Which zones share a border, as a sorted adjacency list. */
export function zoneAdjacency(g: Grid, zoneOf: Int32Array, count: number): number[][] {
  const sets: Set<number>[] = [];
  for (let z = 0; z < count; z++) sets.push(new Set<number>());
  const buf: number[] = [];
  for (let i = 0; i < zoneOf.length; i++) {
    const a = zoneOf[i]!;
    for (const nb of orthoNeighbours(g, i, buf)) {
      const b = zoneOf[nb]!;
      if (a !== b) sets[a]!.add(b);
    }
  }
  return sets.map((s) => [...s].sort((x, y) => x - y));
}

/**
 * Give every zone a job from the adjacency graph.
 *
 *   starting   a player's own neighbourhood
 *   water      the sea
 *   junction   borders two players, or is the one corridor out of a start
 *   treasure   interior land that touches no player
 *   dead-end   a land leaf: one neighbour, the prize behind a choke
 *
 * A map with no interior can fail to grow a treasure zone. In that case the
 * land neutral farthest from any player seed is promoted, so the object pass
 * always has somewhere that is allowed to hold a bank.
 */
export function assignZoneJobs(
  g: Grid, zones: Zone[], adjacency: readonly (readonly number[])[],
): void {
  const players = zones.filter((z) => z.role === "player");
  for (const z of zones) {
    if (z.role === "player") { z.job = "starting"; continue; }
    if (z.role === "water" || z.terrain === "water") { z.job = "water"; continue; }
    const nbs = adjacency[z.id] ?? [];
    let playerNbs = 0, landNbs = 0;
    for (const n of nbs) {
      const o = zones[n];
      if (o === undefined) continue;
      if (o.role === "player") playerNbs++;
      if (o.role !== "water" && o.terrain !== "water") landNbs++;
    }
    if (landNbs <= 1) z.job = "dead-end";
    else if (playerNbs >= 2) z.job = "junction";
    else if (playerNbs === 0) z.job = "treasure";
    else z.job = "junction";
  }
  if (zones.some((z) => z.job === "treasure")) return;
  let best: Zone | null = null;
  let bestD = -1;
  for (const z of zones) {
    if (z.job === "water" || z.job === "starting") continue;
    let nearest = 0x3fffffff;
    for (const p of players) {
      const d = Math.max(
        Math.abs(xOf(g, z.seed) - xOf(g, p.seed)),
        Math.abs(yOf(g, z.seed) - yOf(g, p.seed)));
      if (d < nearest) nearest = d;
    }
    if (nearest > bestD || (nearest === bestD && best !== null && z.id < best.id)) {
      best = z;
      bestD = nearest;
    }
  }
  if (best !== null) best.job = "treasure";
}

// ------------------------------------------------------------------ painting

/**
 * Give every zone a terrain. Player zones take their faction's native terrain;
 * the rest are chosen so that no zone ends up beside a terrain it may not
 * touch, walking the zones in a fixed order so the result is a function of the
 * seed alone.
 */
export function assignTerrain(
  zones: Zone[], adjacency: readonly (readonly number[])[], r: Rng,
  nativeOf: readonly TerrainKind[],
): void {
  for (const z of zones) {
    if (z.role === "water") z.terrain = "water";
    else if (z.role === "player") {
      const t = nativeOf[z.player] ?? "grass";
      z.terrain = t === "water" || t === "rock" ? "grass" : t;
    } else z.terrain = "rock"; // sentinel: "not yet assigned"
  }

  for (const z of zones) {
    if (z.terrain !== "rock") continue;
    const fixed: TerrainKind[] = [];
    for (const nb of adjacency[z.id] ?? []) {
      const t = zones[nb]?.terrain;
      if (t !== undefined && t !== "rock") fixed.push(t);
    }
    const options = LAND.filter((t) => fixed.every((f) => plausible(t, f)));
    // A zone wedged between three incompatible neighbours takes the terrain
    // that clashes with fewest of them; the seam pass paints the rest out.
    if (options.length > 0) {
      z.terrain = pick(r, options) ?? "grass";
    } else {
      let best: TerrainKind = "dirt", bestBad = 99;
      for (const t of LAND) {
        const bad = fixed.filter((f) => !plausible(t, f)).length;
        if (bad < bestBad) { bestBad = bad; best = t; }
      }
      z.terrain = best;
    }
  }
}

export function paintZones(m: AdventureMapState, zones: readonly Zone[]): void {
  for (const z of zones) {
    for (const i of z.cells) {
      setTerrain(m, xOf(m, i), yOf(m, i), 0, z.terrain);
    }
  }
}

/**
 * Paint a bridging terrain along any seam between two terrains that may not
 * touch. Two passes over a snapshot, so the band is two cells wide and the
 * result does not depend on which cell was visited first.
 */
export function smoothSeams(m: AdventureMapState): void {
  const g: Grid = { width: m.width, height: m.height };
  const buf: number[] = [];
  const waterIndex = TERRAINS.indexOf("water");
  for (let pass = 0; pass < 2; pass++) {
    const before = Uint8Array.from(m.terrain);
    const beachBuf: number[] = [];
    const isBeach = (i: number): boolean => {
      if (TERRAINS[before[i]!] !== "sand") return false;
      for (const nb of orthoNeighbours(g, i, beachBuf)) if (before[nb] === waterIndex) return true;
      return false;
    };
    for (let i = 0; i < before.length; i++) {
      const a = TERRAINS[before[i]!]!;
      if (a === "water" || a === "rock") continue;
      // A beach is the transition, not a clash: leave it alone.
      if (isBeach(i)) continue;
      let clash: TerrainKind | null = null;
      for (const nb of orthoNeighbours(g, i, buf)) {
        const b = TERRAINS[before[nb]!]!;
        if (b === "rock") continue;
        if (!plausible(a, b)) { clash = b; break; }
      }
      if (clash === null) continue;
      const bridge = bridgeTerrain(a, clash);
      if (bridge !== null) setTerrain(m, xOf(g, i), yOf(g, i), 0, bridge);
    }
  }
}

/** Land that touches water becomes beach. A coast should read as a coast. */
export function paintBeaches(m: AdventureMapState): void {
  const g: Grid = { width: m.width, height: m.height };
  const before = Uint8Array.from(m.terrain);
  const waterIndex = TERRAINS.indexOf("water");
  const buf: number[] = [];
  const beachable: readonly TerrainKind[] = ["grass", "dirt", "swamp", "rough"];
  for (let i = 0; i < before.length; i++) {
    const t = TERRAINS[before[i]!]!;
    if (!beachable.includes(t)) continue;
    let touchesWater = false;
    for (const nb of orthoNeighbours(g, i, buf)) {
      if (before[nb] === waterIndex) { touchesWater = true; break; }
    }
    if (touchesWater) setTerrain(m, xOf(g, i), yOf(g, i), 0, "sand");
  }
}

/**
 * Ragged the zone boundaries.
 *
 * Two zones grown at once collide along a front that is very nearly straight,
 * and a straight terrain boundary is the tell that a map was generated. This
 * lets a cell defect to a terrain that already surrounds it, which erodes the
 * front into a coastline without moving any zone anywhere.
 */
export function erodeBoundaries(m: AdventureMapState, r: Rng, passes: number): void {
  const g: Grid = { width: m.width, height: m.height };
  const buf: number[] = [];
  for (let pass = 0; pass < passes; pass++) {
    const before = Uint8Array.from(m.terrain);
    for (let i = 0; i < before.length; i++) {
      const counts = new Int32Array(TERRAINS.length);
      for (const nb of allNeighbours(g, i, buf)) counts[before[nb]!]! ++;
      let best = -1, bestCount = 2;
      for (let t = 0; t < counts.length; t++) {
        if (t === before[i]) continue;
        if (counts[t]! > bestCount) { bestCount = counts[t]!; best = t; }
      }
      if (best < 0) continue;
      if (int(r, 10000) >= 3000) continue;
      setTerrain(m, xOf(g, i), yOf(g, i), 0, TERRAINS[best]!);
    }
  }
}

/**
 * Drop one-cell water specks and one-cell land specks. A lake with a hole in it
 * and a puddle in a meadow both read as noise; the eye only accepts water in
 * connected bodies.
 */
export function despeckle(m: AdventureMapState): void {
  const g: Grid = { width: m.width, height: m.height };
  const waterIndex = TERRAINS.indexOf("water");
  const buf: number[] = [];
  for (let pass = 0; pass < 2; pass++) {
    const before = Uint8Array.from(m.terrain);
    for (let i = 0; i < before.length; i++) {
      const nbs = allNeighbours(g, i, buf);
      let same = 0;
      let majority = before[i]!;
      const counts = new Map<number, number>();
      for (const nb of nbs) {
        const t = before[nb]!;
        counts.set(t, (counts.get(t) ?? 0) + 1);
        if (t === before[i]!) same++;
      }
      if (same >= 2) continue;
      let best = -1;
      for (const key of [...counts.keys()].sort((a, b) => a - b)) {
        const c = counts.get(key)!;
        if (c > best) { best = c; majority = key; }
      }
      if (majority === before[i]!) continue;
      // Never let despeckling open a hole in the middle of a lake shore.
      if (majority === waterIndex && before[i] !== waterIndex && best < 5) continue;
      setTerrain(m, xOf(g, i), yOf(g, i), 0, TERRAINS[majority]!);
    }
  }
}

/** Positional art variant, `(y mod 4) * 4 + (x mod 4)`; see presentation.md. */
export function setVariants(m: AdventureMapState): void {
  for (let y = 0; y < m.height; y++) {
    for (let x = 0; x < m.width; x++) setVariant(m, x, y, 0, (y % 4) * 4 + (x % 4));
  }
}
